import { Element, type PinSlot } from './element';
import type { MnaMatrix } from './mna';
import { fmt } from './format';
import { instantiateElements, serializeElements, type SerializedElement } from './serial';

/* ═══════════════════════════ 모듈(서브회로) 정의 ═══════════════════════════ */

export type PortSide = 'left' | 'right';

export interface PortInfo {
  name: string;
  side: PortSide;
}

/** 모듈 정의 — 회로 한 벌 + 바깥으로 나가는 포트 목록 */
export interface SubcktDef {
  id: string;
  name: string;
  version: number;
  ports: PortInfo[];
  elements: SerializedElement[];
  /** 모듈 상자 색 (선택) */
  color?: string;
  description?: string;
}

export function newDefId(): string {
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** 포트 목록을 정해진 순서로 정렬: 왼쪽 → 오른쪽, 각 변은 위에서 아래로 */
function sortPorts(ports: Port[]): Port[] {
  const bySide = (side: PortSide) =>
    ports.filter((p) => p.side === side).sort((a, b) => a.y1 - b.y1 || a.x1 - b.x1);
  return [...bySide('left'), ...bySide('right')];
}

export interface DefResult {
  def?: SubcktDef;
  error?: string;
}

/** 소자 목록으로 모듈 정의를 만든다 (포트 부품이 최소 1개 있어야 함) */
export function makeDef(elements: Element[], name: string, id = newDefId(), version = 1): DefResult {
  const ports = elements.filter((e): e is Port => e instanceof Port);
  if (ports.length === 0) {
    return { error: '포트(단자) 부품이 하나도 없어요. 바깥과 연결될 곳에 포트를 놓아주세요.' };
  }
  const names = new Set<string>();
  for (const p of ports) {
    if (!p.portName) return { error: '이름이 비어 있는 포트가 있어요.' };
    if (names.has(p.portName)) return { error: `포트 이름 "${p.portName}" 이(가) 겹쳐요.` };
    names.add(p.portName);
  }
  const sorted = sortPorts(ports);
  return {
    def: {
      id,
      name: name.trim() || '내 모듈',
      version,
      ports: sorted.map((p) => ({ name: p.portName, side: p.side })),
      elements: serializeElements(elements),
    },
  };
}

/* ═══════════════════════════ 포트 ═══════════════════════════ */

/**
 * 포트(단자) — 모듈 안에서 "여기가 바깥과 이어지는 곳" 표시.
 * 모듈 인스턴스 안에서는 안쪽 점과 바깥 핀을 잇는 저항 0 연결이 되고,
 * 그 연결로 흐르는 전류가 곧 포트 전류가 된다.
 */
export class Port extends Element {
  override get pinCount(): number {
    return 1;
  }
  override get idealConductor(): boolean {
    return true;
  }
  get portName(): string {
    return this.str('name', '').trim();
  }
  get side(): PortSide {
    return this.str('side', 'left') === 'right' ? 'right' : 'left';
  }
  /** 이 포트의 전압 (접지 기준) */
  get voltage(): number {
    return this.voltageDrop;
  }
  info(): string[] {
    return [
      `포트 ${this.portName || '(이름 없음)'}`,
      `위치 ${this.side === 'left' ? '왼쪽' : '오른쪽'}`,
      `전압 ${fmt(this.voltage, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
    ];
  }
}

/* ═══════════════════════════ 모듈 인스턴스 ═══════════════════════════ */

/**
 * 회로에 놓인 모듈 하나. 스스로 스탬프하지 않고, Circuit.build() 가
 * 안쪽 소자들(inner)을 펼쳐서 함께 푼다. 안쪽 소자는 인스턴스마다 따로라서
 * 같은 모듈을 여러 개 놓아도 배터리 잔량 같은 상태가 각자 유지된다.
 */
export class SubcktInstance extends Element {
  inner: Element[] = [];
  private innerKey = '';
  private portCache: { raw: string; list: PortInfo[] } = { raw: '', list: [] };

  get defId(): string {
    return this.str('defId', '');
  }
  get label(): string {
    return this.str('name', '모듈');
  }
  get ports(): PortInfo[] {
    const raw = this.str('ports', '[]');
    if (raw !== this.portCache.raw) {
      let list: PortInfo[] = [];
      try {
        const parsed = JSON.parse(raw) as { name: string; side: string }[];
        list = parsed.map((p) => ({ name: String(p.name), side: p.side === 'right' ? 'right' : 'left' }));
      } catch {
        list = [];
      }
      this.portCache = { raw, list };
    }
    return this.portCache.list;
  }

  override get pinCount(): number {
    return this.ports.length;
  }
  override get usesLayout(): boolean {
    return true;
  }
  /** 한 변에 놓이는 포트 수의 최댓값 (상자 높이 계산용) */
  get rows(): number {
    const l = this.ports.filter((p) => p.side === 'left').length;
    const r = this.ports.length - l;
    return Math.max(1, l, r);
  }

  override pinLayout(): PinSlot[] {
    const left = this.ports.filter((p) => p.side === 'left');
    const right = this.ports.filter((p) => p.side === 'right');
    const slots = (list: PortInfo[], t: number | 'end'): PinSlot[] =>
      list.map((p, i) => ({ t, s: 2 * i - (list.length - 1), label: p.name }));
    return [...slots(left, 0), ...slots(right, 'end')];
  }

  /** 핀 순서와 같은 순서의 포트 이름 */
  get portOrder(): string[] {
    return this.pinLayout().map((s) => s.label);
  }

  /** 정의가 바뀌었으면 안쪽 회로를 새로 만든다. 새로 만들었으면 true */
  ensureInner(def: SubcktDef): boolean {
    const key = `${def.id}@${def.version}`;
    if (key === this.innerKey && this.inner.length > 0) return false;
    this.inner = instantiateElements(def.elements, false);
    for (const el of this.inner) el.resetState();
    this.innerKey = key;
    return true;
  }

  /** 정의에 맞춰 포트/이름 속성을 갱신 */
  syncWithDef(def: SubcktDef): void {
    this.props.ports = JSON.stringify(def.ports);
    this.props.name = def.name;
    this.props.defId = def.id;
    if (def.color) this.props.color = def.color;
  }

  override resetState(): void {
    for (const el of this.inner) {
      el.resetState();
      el.current = 0;
      el.voltageDrop = 0;
    }
  }

  override stamp(_m: MnaMatrix): void {
    /* 안쪽 소자들이 대신 스탬프한다 */
  }

  /** 안쪽 포트 소자 (이름 → 소자) */
  portElements(): Map<string, Port> {
    const map = new Map<string, Port>();
    for (const el of this.inner) if (el instanceof Port && !map.has(el.portName)) map.set(el.portName, el);
    return map;
  }

  override pinCurrents(): number[] {
    // 포트 전류: 안쪽 → 바깥 방향이 양수 → 소자로 '들어가는' 전류는 그 반대
    const map = this.portElements();
    return this.portOrder.map((n) => -(map.get(n)?.current ?? 0));
  }

  /** 안쪽 소자들의 소비 전력 합 (전원은 음수로 들어가므로 순손실 ≈ 발열) */
  get innerDissipation(): number {
    let sum = 0;
    for (const el of this.inner) {
      if (el instanceof Port || el instanceof SubcktInstance) continue;
      const p = el.power;
      if (Number.isFinite(p) && p > 0) sum += p;
    }
    return sum;
  }

  info(): string[] {
    const lines = [`모듈: ${this.label}`];
    const map = this.portElements();
    for (const name of this.portOrder) {
      const p = map.get(name);
      if (!p) {
        lines.push(`${name}: (연결 안 됨)`);
        continue;
      }
      const i = p.current;
      const dir = Math.abs(i) < 1e-9 ? '' : i > 0 ? ' ↑나감' : ' ↓들어옴';
      lines.push(`${name}: ${fmt(p.voltage, 'V')} · ${fmt(Math.abs(i), 'A')}${dir}`);
    }
    // 안쪽의 주요 상태 (배터리 잔량, 충전 상태 등)
    for (const el of this.inner) {
      if (!/^(battery|liion|charger|dcdc|bms|subckt)$/.test(el.type)) continue;
      const info = el.info();
      lines.push(`· ${info[0]}: ${info[1] ?? ''}${el.type === 'liion' || el.type === 'battery' ? ` · ${info.find((l) => l.startsWith('잔량')) ?? ''}` : ''}`);
    }
    lines.push(`내부 소자 ${this.inner.length}개`);
    return lines;
  }
}
